import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type Query,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { myWorkKeys } from "@/features/my-work/api";
import { projectKeys } from "@/features/projects/api";
import { allTasksQueryOf, type AllTasksFilters } from "@/features/tasks/lib/allTasksParams";
import type { MovePlan, Task } from "@/features/tasks/lib/board";
import { searchQueryOf, type TaskListFilters } from "@/features/tasks/lib/listParams";
import { PENDING_SERIES_ID } from "@/features/tasks/lib/recurrence";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapPage, type PageOf } from "@/shared/api/page";
import { makeKeys } from "@/shared/api/queryKeys";

export type TaskCreate = Schemas["TaskCreate"];
export type TaskUpdate = Schemas["TaskUpdate"];
export type StandaloneTaskCreate = Schemas["StandaloneTaskCreate"];

/** Lists hold tasks (a project's board, a filtered list); a detail holds one task by reference. */
export const taskKeys = makeKeys("tasks");

const BOARD_VIEW = "board";
const SEARCH_VIEW = "search";
const SUBTASKS_VIEW = "subtasks";
const SEARCH_PAGE_SIZE = 50;

/** A project's board: every task of the project, in board order. */
export function boardKey(projectKey: string) {
  return taskKeys.list({ project: projectKey, view: BOARD_VIEW });
}

export function searchKey(projectKey: string, filters: TaskListFilters) {
  return taskKeys.list({
    project: projectKey,
    view: SEARCH_VIEW,
    statuses: filters.statuses,
    assignee: filters.assignee,
    priorities: filters.priorities,
    dueAfter: filters.dueAfter,
    dueBefore: filters.dueBefore,
    q: filters.q,
    sort: filters.sort,
    labels: filters.labels,
    milestone: filters.milestone,
    phase: filters.phase,
    blocked: filters.blocked,
  });
}

/** A task's subtasks, in the order they were added. */
export function subtasksKey(projectKey: string | null, parentReference: string) {
  return taskKeys.list({ project: projectKey, view: SUBTASKS_VIEW, parent: parentReference });
}

export function useBoardTasks(projectKey: string, enabled = true) {
  return useQuery({
    queryKey: boardKey(projectKey),
    queryFn: () =>
      unwrap(api.GET("/api/v1/projects/{key}/tasks", { params: { path: { key: projectKey } } })),
    enabled: enabled && projectKey !== "",
  });
}

type TaskPages = InfiniteData<PageOf<Task>, string | null>;

/** A project's tasks, filtered and sorted on the server, a page at a time. */
export function useTaskSearch(projectKey: string, filters: TaskListFilters) {
  return useInfiniteQuery({
    queryKey: searchKey(projectKey, filters),
    queryFn: ({ pageParam }) =>
      unwrapPage(
        api.GET("/api/v1/tasks", {
          params: {
            query: {
              ...searchQueryOf(projectKey, filters),
              cursor: pageParam,
              limit: SEARCH_PAGE_SIZE,
            },
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
}

const ALL_VIEW = "all";
const ALL_PAGE_SIZE = 200;
/** Cards on the all-projects board at most; past this the filters should narrow it. */
export const ALL_TASKS_CAP = 1000;

export function allTasksKey(filters: AllTasksFilters, today: string) {
  return taskKeys.list({ view: ALL_VIEW, today, ...filters });
}

/**
 * Tasks across every visible project, a page at a time until the end or the cap. One task
 * past the cap is kept, so the board can tell there is more.
 */
export function useAllTasks(filters: AllTasksFilters, today: string) {
  return useQuery({
    queryKey: allTasksKey(filters, today),
    queryFn: async () => {
      const tasks: Task[] = [];
      let cursor: string | null = null;
      do {
        const page: PageOf<Task> = await unwrapPage(
          api.GET("/api/v1/tasks", {
            params: {
              query: { ...allTasksQueryOf(filters, today), cursor, limit: ALL_PAGE_SIZE },
            },
          }),
        );
        tasks.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor !== null && tasks.length <= ALL_TASKS_CAP);
      return tasks.slice(0, ALL_TASKS_CAP + 1);
    },
    placeholderData: keepPreviousData,
  });
}

/* ---- The task cache: the same task can sit in several lists and in its own detail. ---- */

type TaskChanges = Partial<Task>;
/** The cached queries an optimistic edit changed, as they were before it. */
type Snapshot = [QueryKey, unknown][];

function isTask(value: unknown): value is Task {
  return typeof value === "object" && value !== null && "id" in value && "version" in value;
}

function isTaskPages(value: unknown): value is TaskPages {
  return typeof value === "object" && value !== null && "pages" in value;
}

/** The tasks one cached query holds: a board many, a paged list many, a detail one. */
function tasksIn(data: unknown): Task[] {
  if (Array.isArray(data)) return (data as unknown[]).filter(isTask);
  if (isTaskPages(data)) return data.pages.flatMap((page) => page.items);
  return isTask(data) ? [data] : [];
}

function holdsTask(data: unknown, taskId: string): boolean {
  return tasksIn(data).some((task) => task.id === taskId);
}

/** The query's data with the task changed, or undefined (leave it alone) if it lacks the task. */
function withChanges(data: unknown, taskId: string, changes: TaskChanges): unknown {
  if (!holdsTask(data, taskId)) return undefined;
  const patch = (task: Task): Task => (task.id === taskId ? { ...task, ...changes } : task);
  if (Array.isArray(data))
    return (data as unknown[]).map((item) => (isTask(item) ? patch(item) : item));
  if (isTaskPages(data)) {
    return {
      ...data,
      pages: data.pages.map((page) => ({ ...page, items: page.items.map(patch) })),
    };
  }
  return isTask(data) ? patch(data) : data;
}

/** Change one task in every cached list and detail that holds it. */
function patchTask(queryClient: QueryClient, taskId: string, changes: TaskChanges): void {
  queryClient.setQueriesData<unknown>({ queryKey: taskKeys.all }, (data: unknown) =>
    withChanges(data, taskId, changes),
  );
}

/** The newest copy of a task in any cached list or detail. */
function cachedTask(
  queryClient: QueryClient,
  matches: (task: Task) => boolean,
): { task: Task; updatedAt: number } | undefined {
  let newest: { task: Task; updatedAt: number } | undefined;
  for (const query of queryClient.getQueryCache().findAll({ queryKey: taskKeys.all })) {
    for (const task of tasksIn(query.state.data)) {
      if (matches(task) && (newest === undefined || task.version > newest.task.version)) {
        newest = { task, updatedAt: query.state.dataUpdatedAt };
      }
    }
  }
  return newest;
}

/** The If-Match value for an edit, read when the request is sent (see editScope). */
function versionToSend(queryClient: QueryClient, taskId: string, fallback?: number): string {
  const version = cachedTask(queryClient, (task) => task.id === taskId)?.task.version ?? fallback;
  if (version === undefined) throw new Error(`No version known for task ${taskId}`);
  return String(version);
}

/** One task by its reference (NOON-12), from any project; a cached copy shows at once. */
export function useTask(reference: string | null) {
  const queryClient = useQueryClient();
  const wanted = reference?.toUpperCase() ?? "";
  const cached = () => cachedTask(queryClient, (task) => task.reference.toUpperCase() === wanted);
  return useQuery({
    queryKey: taskKeys.detail(wanted),
    queryFn: () =>
      unwrap(api.GET("/api/v1/tasks/{task_ref}", { params: { path: { task_ref: wanted } } })),
    initialData: () => cached()?.task,
    initialDataUpdatedAt: () => cached()?.updatedAt,
    enabled: wanted !== "",
  });
}

/** Show an edit at once everywhere the task is cached; the snapshot undoes it. */
async function applyOptimistically(
  queryClient: QueryClient,
  taskId: string,
  changes: TaskChanges,
): Promise<Snapshot> {
  const filters = {
    queryKey: taskKeys.all,
    predicate: (query: Query) => holdsTask(query.state.data, taskId),
  };
  await queryClient.cancelQueries(filters);
  const snapshot = queryClient.getQueriesData(filters);
  patchTask(queryClient, taskId, changes);
  return snapshot;
}

function rollBack(queryClient: QueryClient, snapshot: Snapshot | undefined): void {
  for (const [queryKey, data] of snapshot ?? []) queryClient.setQueryData(queryKey, data);
}

/*
 * Edits to a project's tasks reach the server one at a time, and each request reads the task's
 * version from the cache when it is sent, so it carries the version the previous edit returned.
 * Without this, renaming a task and then quickly moving it would fail its own version check.
 * Optimistic updates still apply at once; only the requests wait in line.
 */
function editScope(projectKey: string | null) {
  return { id: `task-edits:${projectKey ?? "any"}` };
}

/**
 * After an edit, refresh the task lists (an edit can move a task in or out of a filtered list)
 * and the project's counts. The lists wait until the project's queue of edits is empty, so a
 * refetch never overwrites an edit that is still on its way.
 */
function useSettleEdit(projectKey: string | null) {
  const queryClient = useQueryClient();
  return () => {
    const scopeId = editScope(projectKey).id;
    const inLine = queryClient.getMutationCache().findAll({
      status: "pending",
      predicate: (mutation) => mutation.options.scope?.id === scopeId,
    }).length;
    // The edit settling right now still counts as pending.
    if (inLine <= 1) void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    if (projectKey === null)
      void queryClient.invalidateQueries({ queryKey: projectKeys.details() });
    else
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectKey), exact: true });
  };
}

export function useCreateTask(projectKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskCreate) =>
      unwrap(
        api.POST("/api/v1/projects/{key}/tasks", { params: { path: { key: projectKey } }, body }),
      ),
    onSuccess: (task) => refreshAfterCreate(queryClient, task),
  });
}

/** Show a new task on its project's board, or in My Work and the lists when it has none. */
function refreshAfterCreate(queryClient: QueryClient, task: Task) {
  const projectKey = task.project?.key ?? null;
  void queryClient.invalidateQueries({ queryKey: myWorkKeys.all });
  if (projectKey === null) return queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
  queryClient.setQueryData<Task[]>(boardKey(projectKey), (tasks = []) => [...tasks, task]);
  void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectKey), exact: true });
  return queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
}

/** Create a task from anywhere: in the project `project_key` names, or in no project. */
export function useCreateStandaloneTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StandaloneTaskCreate) => unwrap(api.POST("/api/v1/tasks", { body })),
    onSuccess: (task) => refreshAfterCreate(queryClient, task),
  });
}

export interface MoveToProjectVariables {
  task: Task;
  projectKey: string;
}

/** Bring a task outside any project (and its subtasks) into one; it gets the project's next number. */
export function useMoveTaskToProject() {
  const queryClient = useQueryClient();
  return useMutation({
    scope: editScope(null),
    mutationFn: ({ task, projectKey }: MoveToProjectVariables) =>
      unwrap(
        api.POST("/api/v1/tasks/{task_id}/project", {
          params: {
            path: { task_id: task.id },
            header: { "if-match": versionToSend(queryClient, task.id, task.version) },
          },
          body: { project_key: projectKey },
        }),
      ),
    onSuccess: (moved, { projectKey }) => {
      queryClient.setQueryData(taskKeys.detail(moved.reference.toUpperCase()), moved);
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: myWorkKeys.all });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectKey), exact: true });
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export interface MoveTaskVariables {
  plan: MovePlan;
  /** Used for If-Match only when the task is in no cached list or detail. */
  version?: number;
}

/**
 * Move optimistically; roll back if the server disagrees, and the caller reloads the board.
 * Null serves a board of many projects: its moves share one queue.
 */
export function useMoveTask(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);

  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ plan, version }: MoveTaskVariables) =>
      unwrap(
        api.POST("/api/v1/tasks/{task_id}/move", {
          params: {
            path: { task_id: plan.taskId },
            header: { "if-match": versionToSend(queryClient, plan.taskId, version) },
          },
          body: { status: plan.status, after_id: plan.afterId, before_id: plan.beforeId },
        }),
      ),
    onMutate: ({ plan }) =>
      applyOptimistically(queryClient, plan.taskId, {
        status: plan.status,
        position: plan.position,
      }),
    // Take only what a move changes, so a queued rename's optimistic title stays on screen.
    onSuccess: (moved) => {
      patchTask(queryClient, moved.id, {
        status: moved.status,
        position: moved.position,
        completed_at: moved.completed_at,
        updated_at: moved.updated_at,
        version: moved.version,
      });
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

export interface UpdateTaskVariables {
  taskId: string;
  /** The request body. */
  changes: TaskUpdate;
  /** How the task looks with the edit, shown at once. */
  optimistic: TaskChanges;
  /** Used for If-Match only when the task is in no cached list or detail. */
  version?: number;
}

/** The fields of the server's copy that an edit touched, plus its new version. */
function editedFields(saved: Task, optimistic: TaskChanges): TaskChanges {
  const fields = Object.keys(optimistic) as (keyof Task)[];
  return {
    ...Object.fromEntries(fields.map((field) => [field, saved[field]])),
    updated_at: saved.updated_at,
    version: saved.version,
  };
}

/** Edit a task optimistically. The version guards against overwriting someone else's edit. */
export function useUpdateTask(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);

  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ taskId, changes, version }: UpdateTaskVariables) =>
      unwrap(
        api.PATCH("/api/v1/tasks/{task_id}", {
          params: {
            path: { task_id: taskId },
            header: { "if-match": versionToSend(queryClient, taskId, version) },
          },
          body: changes,
        }),
      ),
    onMutate: ({ taskId, optimistic }) => applyOptimistically(queryClient, taskId, optimistic),
    // Take only what this edit changed, so another queued edit's optimistic value stays.
    onSuccess: (saved, { optimistic }) => {
      patchTask(queryClient, saved.id, editedFields(saved, optimistic));
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

type Lifecycle = "cancel" | "restore";

const LIFECYCLE_PATH = {
  cancel: "/api/v1/tasks/{task_id}/cancel",
  restore: "/api/v1/tasks/{task_id}/restore",
} as const;

/* Restoring puts a task back at the end of To do. */
const LIFECYCLE_STATUS = { cancel: "cancelled", restore: "todo" } as const;

/** Cancel a task (it leaves the board) or restore a cancelled one, optimistically. */
export function useTaskLifecycle(projectKey: string | null, action: Lifecycle) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);

  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ taskId, version }: { taskId: string; version?: number }) =>
      unwrap(
        api.POST(LIFECYCLE_PATH[action], {
          params: {
            path: { task_id: taskId },
            header: { "if-match": versionToSend(queryClient, taskId, version) },
          },
        }),
      ),
    onMutate: ({ taskId }) =>
      applyOptimistically(queryClient, taskId, { status: LIFECYCLE_STATUS[action] }),
    onSuccess: (saved) => {
      patchTask(queryClient, saved.id, {
        status: saved.status,
        position: saved.position,
        completed_at: saved.completed_at,
        updated_at: saved.updated_at,
        version: saved.version,
      });
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

/* ---- Subtasks and labels ---- */

export function useSubtasks(parent: Pick<Task, "reference" | "project">, enabled = true) {
  return useQuery({
    queryKey: subtasksKey(parent.project?.key ?? null, parent.reference),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/tasks/{task_ref}/subtasks", {
          params: { path: { task_ref: parent.reference } },
        }),
      ),
    enabled,
  });
}

/** Refresh what shows a parent's subtasks: their list, its counts, and the board. */
function refreshParent(
  queryClient: QueryClient,
  projectKey: string | null,
  parentReference: string,
) {
  void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: taskKeys.detail(parentReference.toUpperCase()) });
  if (projectKey !== null)
    void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectKey), exact: true });
  else void queryClient.invalidateQueries({ queryKey: myWorkKeys.all });
}

/** Add a subtask under a top-level task; it lands at the end of To do. */
export function useCreateSubtask(parent: Pick<Task, "id" | "reference" | "project">) {
  const queryClient = useQueryClient();
  const projectKey = parent.project?.key ?? null;
  return useMutation({
    mutationFn: (title: string) =>
      unwrap(
        projectKey === null
          ? api.POST("/api/v1/tasks", { body: { title, parent_id: parent.id } })
          : api.POST("/api/v1/projects/{key}/tasks", {
              params: { path: { key: projectKey } },
              body: { title, parent_id: parent.id },
            }),
      ),
    onSuccess: (subtask) => {
      queryClient.setQueryData<Task[]>(subtasksKey(projectKey, parent.reference), (tasks = []) => [
        ...tasks,
        subtask,
      ]);
      refreshParent(queryClient, projectKey, parent.reference);
    },
  });
}

/** Tick a subtask off (to Done) or back (to To do), shown at once like any move. */
export function useToggleSubtask(parent: Pick<Task, "reference" | "project">) {
  const queryClient = useQueryClient();
  const projectKey = parent.project?.key ?? null;
  const moveTask = useMoveTask(projectKey);
  return (subtask: Task, done: boolean) => {
    const status = done ? "done" : "todo";
    moveTask.mutate(
      {
        plan: {
          taskId: subtask.id,
          status,
          afterId: null,
          beforeId: null,
          position: subtask.position,
        },
        version: subtask.version,
      },
      {
        onSettled: () => {
          refreshParent(queryClient, projectKey, parent.reference);
        },
      },
    );
  };
}

export interface SetLabelsVariables {
  task: Task;
  labels: Task["labels"];
}

/** Replace a task's labels with this set, optimistically, with the task's version. */
export function useSetTaskLabels(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ task, labels }: SetLabelsVariables) =>
      unwrap(
        api.PUT("/api/v1/tasks/{task_id}/labels", {
          params: {
            path: { task_id: task.id },
            header: { "if-match": versionToSend(queryClient, task.id, task.version) },
          },
          body: { label_ids: labels.map((label) => label.id) },
        }),
      ),
    onMutate: ({ task, labels }) => applyOptimistically(queryClient, task.id, { labels }),
    onSuccess: (saved) => {
      patchTask(queryClient, saved.id, {
        labels: saved.labels,
        updated_at: saved.updated_at,
        version: saved.version,
      });
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

export interface SetAssigneesVariables {
  task: Task;
  assignees: Task["assignees"];
}

/** Replace who does a task with this set, optimistically, with the task's version. */
export function useSetTaskAssignees(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ task, assignees }: SetAssigneesVariables) =>
      unwrap(
        api.PUT("/api/v1/tasks/{task_id}/assignees", {
          params: {
            path: { task_id: task.id },
            header: { "if-match": versionToSend(queryClient, task.id, task.version) },
          },
          body: { assignee_ids: assignees.map((person) => person.id) },
        }),
      ),
    onMutate: ({ task, assignees }) => applyOptimistically(queryClient, task.id, { assignees }),
    onSuccess: (saved) => {
      patchTask(queryClient, saved.id, {
        assignees: saved.assignees,
        updated_at: saved.updated_at,
        version: saved.version,
      });
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

/* ---- Dependencies ---- */

/** What a task waits for (blocked by) and what waits for it (blocking). */
export function dependenciesKey(reference: string) {
  return [...taskKeys.detail(reference.toUpperCase()), "dependencies"] as const;
}

function dependenciesQuery(reference: string) {
  return {
    queryKey: dependenciesKey(reference),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/tasks/{task_ref}/dependencies", {
          params: { path: { task_ref: reference } },
        }),
      ),
  };
}

export function useDependencies(task: Pick<Task, "reference">) {
  return useQuery(dependenciesQuery(task.reference));
}

/** A task's links, from the cache when it is fresh enough (the board's reminder uses it). */
export function fetchDependencies(queryClient: QueryClient, reference: string) {
  return queryClient.query({ ...dependenciesQuery(reference), staleTime: 10_000 });
}

/**
 * After a link changes, refresh what shows its counts: every task list and detail. Like
 * useSettleEdit, the refresh waits until the project's queue of edits is empty.
 */
function useSettleLinks(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);
  return () => {
    settle();
    const inLine = queryClient.getMutationCache().findAll({
      status: "pending",
      predicate: (mutation) => mutation.options.scope?.id === editScope(projectKey).id,
    }).length;
    if (inLine <= 1) void queryClient.invalidateQueries({ queryKey: taskKeys.details() });
  };
}

type Dependencies = Schemas["DependenciesOut"];
type DependenciesSnapshot = { links: Dependencies | undefined; tasks: Snapshot };

function linkOf(task: Task): Schemas["TaskLinkOut"] {
  const { id, reference, title, status, assignees } = task;
  return { id, reference, title, status, assignees };
}

function isOpenStatus(status: Task["status"]): boolean {
  return status !== "done" && status !== "cancelled";
}

/** Make a task wait for another, shown at once; a refused link (a cycle) rolls back. */
export function useAddDependency(task: Task) {
  const queryClient = useQueryClient();
  const projectKey = task.project?.key ?? null;
  const settle = useSettleLinks(projectKey);
  const key = dependenciesKey(task.reference);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: (blocker: Task) =>
      unwrap(
        api.POST("/api/v1/tasks/{task_id}/dependencies", {
          params: { path: { task_id: task.id } },
          body: { blocked_by_id: blocker.id },
        }),
      ),
    onMutate: async (blocker): Promise<DependenciesSnapshot> => {
      await queryClient.cancelQueries({ queryKey: key });
      const links = queryClient.getQueryData<Dependencies>(key);
      if (links) {
        queryClient.setQueryData<Dependencies>(key, {
          ...links,
          blocked_by: [...links.blocked_by, linkOf(blocker)],
        });
      }
      const current = cachedTask(queryClient, (cached) => cached.id === task.id)?.task ?? task;
      const tasks = await applyOptimistically(queryClient, task.id, {
        blocked_by_count: current.blocked_by_count + 1,
        open_blocker_count: current.open_blocker_count + (isOpenStatus(blocker.status) ? 1 : 0),
      });
      return { links, tasks };
    },
    onSuccess: (links) => {
      queryClient.setQueryData(key, links);
    },
    onError: (_error, _blocker, snapshot) => {
      if (snapshot) queryClient.setQueryData(key, snapshot.links);
      rollBack(queryClient, snapshot?.tasks);
    },
    onSettled: settle,
  });
}

export interface RemoveDependencyVariables {
  /** The task at the other end of the link. */
  other: Schemas["TaskLinkOut"];
  /** "blocked_by": this task waits for `other`; "blocking": `other` waits for this task. */
  side: "blocked_by" | "blocking";
}

/** Take a link away, from either end, shown at once. */
export function useRemoveDependency(task: Task) {
  const queryClient = useQueryClient();
  const projectKey = task.project?.key ?? null;
  const settle = useSettleLinks(projectKey);
  const key = dependenciesKey(task.reference);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ other, side }: RemoveDependencyVariables) => {
      const [waiting, blocker] = side === "blocked_by" ? [task.id, other.id] : [other.id, task.id];
      return unwrap(
        api.DELETE("/api/v1/tasks/{task_id}/dependencies/{blocked_by_id}", {
          params: { path: { task_id: waiting, blocked_by_id: blocker } },
        }),
      );
    },
    onMutate: async ({ other, side }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const links = queryClient.getQueryData<Dependencies>(key);
      if (links) {
        queryClient.setQueryData<Dependencies>(key, {
          ...links,
          [side]: links[side].filter((link) => link.id !== other.id),
        });
      }
      return links;
    },
    onError: (_error, _variables, links) => {
      queryClient.setQueryData(key, links);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      settle();
    },
  });
}

/* ---- Repeats ---- */

export type RecurrenceSet = Schemas["RecurrenceSet"];

export interface SetRecurrenceVariables {
  task: Task;
  body: RecurrenceSet;
  /** How the task's series looks with the change, shown at once. */
  optimistic: Task["series"];
}

/** The If-Match value for a series edit: none when the task does not repeat yet. */
function seriesVersionToSend(queryClient: QueryClient, task: Task): string | null {
  const cached = cachedTask(queryClient, (other) => other.id === task.id)?.task ?? task;
  // A series shown before the server made it has no version to match yet.
  const series = cached.series?.id === PENDING_SERIES_ID ? null : cached.series;
  return series ? String(series.version) : null;
}

/** Make a task repeat or change its repeat; new occurrences appear on the board after it. */
export function useSetRecurrence(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: ({ task, body }: SetRecurrenceVariables) =>
      unwrap(
        api.PUT("/api/v1/tasks/{task_id}/recurrence", {
          params: {
            path: { task_id: task.id },
            header: { "if-match": seriesVersionToSend(queryClient, task) },
          },
          body,
        }),
      ),
    onMutate: ({ task, optimistic }) =>
      applyOptimistically(queryClient, task.id, { series: optimistic }),
    onSuccess: (saved) => {
      patchTask(queryClient, saved.id, {
        series: saved.series,
        updated_at: saved.updated_at,
        version: saved.version,
      });
    },
    onError: (_error, _variables, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}

/** Stop a task's repeat; the occurrences already made stay. */
export function useStopRecurrence(projectKey: string | null) {
  const queryClient = useQueryClient();
  const settle = useSettleEdit(projectKey);
  return useMutation({
    scope: editScope(projectKey),
    mutationFn: (task: Task) =>
      unwrap(
        api.DELETE("/api/v1/tasks/{task_id}/recurrence", {
          params: { path: { task_id: task.id } },
        }),
      ),
    onMutate: (task) => applyOptimistically(queryClient, task.id, { series: null }),
    onSuccess: (saved) => {
      patchTask(queryClient, saved.id, {
        series: saved.series,
        updated_at: saved.updated_at,
        version: saved.version,
      });
    },
    onError: (_error, _task, snapshot) => {
      rollBack(queryClient, snapshot);
    },
    onSettled: settle,
  });
}
